import {omit, pick, uniqueId} from 'lodash-es';
import {useReducer} from 'react';

import {processorService} from '@/services/processorService';
import type {AfriexCorridorCost, AfriexTransactionProcessor} from '@/types';
import {getErrorMessage} from '@/utils/errorMessage';
import {showErrorMessage} from '@/utils/showErrorMessage';
import {showSuccessMessage} from '@/utils/showSuccessMessage';

type ProcessorCostsState = {
  costs: AfriexCorridorCost[];
  editingId: string | null;
  editForm: Partial<AfriexCorridorCost>;
  isAdding: boolean;
};

type Action =
  | {type: 'START_ADD'}
  | {type: 'START_EDIT'; cost: AfriexCorridorCost}
  | {type: 'CANCEL_EDIT'}
  | {
      type: 'UPDATE_FORM_FIELD';
      field: keyof AfriexCorridorCost;
      value: unknown;
    }
  | {
      type: 'UPDATE_AMOUNT_FIELD';
      field: keyof AfriexCorridorCost['amount'];
      value: unknown;
    }
  | {type: 'ADD_COST'; cost: AfriexCorridorCost}
  | {type: 'UPDATE_COST'; cost: AfriexCorridorCost}
  | {type: 'DELETE_COST'; costId: string};

const emptyCost = (): Partial<AfriexCorridorCost> => ({
  isActive: true,
  amount: {type: 'PERCENTAGE', value: 0},
});

const reducer = (
  state: ProcessorCostsState,
  action: Action,
): ProcessorCostsState => {
  switch (action.type) {
    case 'START_ADD':
      return {
        ...state,
        editingId: null,
        editForm: emptyCost(),
        isAdding: true,
      };
    case 'START_EDIT':
      return {
        ...state,
        editingId: action.cost.id,
        editForm: {...action.cost},
        isAdding: false,
      };
    case 'CANCEL_EDIT':
      return {
        ...state,
        editingId: null,
        editForm: emptyCost(),
        isAdding: false,
      };
    case 'UPDATE_FORM_FIELD':
      return {
        ...state,
        editForm: {...state.editForm, [action.field]: action.value},
      };
    case 'UPDATE_AMOUNT_FIELD':
      return {
        ...state,
        editForm: {
          ...state.editForm,
          amount: {
            ...(state.editForm.amount ?? {type: 'PERCENTAGE', value: 0}),
            [action.field]: action.value,
          },
        },
      };
    case 'ADD_COST':
      return {...state, costs: [...state.costs, action.cost]};
    case 'UPDATE_COST':
      return {
        ...state,
        costs: state.costs.map((c) =>
          c.id === action.cost.id ? action.cost : c,
        ),
      };
    case 'DELETE_COST':
      return {
        ...state,
        costs: state.costs.map((c) =>
          c.id === action.costId ? {...c, isActive: false} : c,
        ),
      };
    default:
      return state;
  }
};

const useProcessorCosts = (
  processorCode?: AfriexTransactionProcessor,
  initialCosts: AfriexCorridorCost[] = [],
) => {
  const [state, dispatch] = useReducer(reducer, {
    costs: initialCosts,
    editingId: null,
    editForm: emptyCost(),
    isAdding: false,
  });

  const startAdd = () => dispatch({type: 'START_ADD'});

  const startEdit = (cost: AfriexCorridorCost) =>
    dispatch({type: 'START_EDIT', cost});

  const cancelEdit = () => dispatch({type: 'CANCEL_EDIT'});

  const updateFormField = (field: keyof AfriexCorridorCost, value: unknown) =>
    dispatch({type: 'UPDATE_FORM_FIELD', field, value});

  const updateAmountField = (
    field: keyof AfriexCorridorCost['amount'],
    value: unknown,
  ) => dispatch({type: 'UPDATE_AMOUNT_FIELD', field, value});

  const handleSave = async () => {
    if (!processorCode) {
      return;
    }
    if (
      !state.editForm.countryCode ||
      !state.editForm.currencyCode ||
      !state.editForm.channel
    ) {
      showErrorMessage('Country, currency, and channel are required');
      return;
    }
    try {
      if (state.isAdding) {
        const itemToSave = omit(state.editForm, [
          'id',
          'isActive',
          'createdAt',
          'updatedAt',
        ]);
        const withId = {...itemToSave, id: uniqueId()};
        const created = await processorService.createProcessorCost(
          processorCode,
          withId,
        );
        const newItem = {...created, ...withId};
        dispatch({type: 'ADD_COST', cost: newItem});
        showSuccessMessage('Cost added');
      } else if (state.editingId) {
        const itemToUpdate = pick(state.editForm, ['isActive', 'amount']);
        await processorService.updateProcessorCost(
          processorCode,
          state.editingId,
          itemToUpdate,
        );
        dispatch({
          type: 'UPDATE_COST',
          cost: state.editForm as AfriexCorridorCost,
        });
        showSuccessMessage('Cost updated');
      }
      cancelEdit();
    } catch (e: unknown) {
      showErrorMessage(`Error saving cost: ${getErrorMessage(e, String(e))}`);
    }
  };

  const handleDelete = async (costId: string) => {
    if (!processorCode) {
      return;
    }
    try {
      await processorService.updateProcessorCost(processorCode, costId, {
        isActive: false,
      });
      dispatch({type: 'DELETE_COST', costId});
      showSuccessMessage('Cost deleted');
    } catch (e: unknown) {
      showErrorMessage(`Error deleting cost: ${getErrorMessage(e, String(e))}`);
    }
  };

  return {
    costs: state.costs,
    isLoading: false,
    isEditing: state.editingId !== null || state.isAdding,
    editingId: state.editingId,
    isAdding: state.isAdding,
    editForm: state.editForm,
    startAdd,
    startEdit,
    cancelEdit,
    updateFormField,
    updateAmountField,
    handleSave,
    handleDelete,
  };
};

export default useProcessorCosts;
